import React, { useEffect, useRef, useState, useMemo } from "react";
import { Map, View } from "ol";
import { XYZ } from "ol/source";
import TileLayer from "ol/layer/Tile";
import "ol/ol.css";
import { fromLonLat, get } from "ol/proj";
import { defaults } from "ol/interaction";
import { defaults as defaultControls } from "ol/control";
import { useFlightArc } from "./useFlightArc";
import { useEditLayer } from "./useEditLayer";
import { normalizeMapConfig, computeHistoricLayerOpacity } from "./mapWaypoints";
import { getMapThemeConfig } from "./mapThemes";
import { computeCameraState } from "./cameraChoreography";
import { splitMultiSegmentRoute } from "./routeRenderer";
import { CompassRose } from "./CompassRose";
import { LivingPinsOverlay } from "./LivingPinsOverlay";
import { useHistoricMapLayer } from "./useHistoricMapLayer";
import { useMapCameraAnimation } from "./useMapCameraAnimation";
import { prefetchRouteTiles } from "./tilePrefetcher";

export const OpenLayerMap = ({
  mapConfig,
  activeStepIndex = 0,
  progress = 0,
  zoom = 8,
  edit = false,
  onWaypointChange,
  onSelectWaypoint,
  from,
  to,
  onAdd,
  onChange,
  className = "",
}) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const tileLayerRef = useRef(null);

  const normalizedConfig = useMemo(
    () => normalizeMapConfig({ mapConfig, mapCoords: { from, to } }),
    [mapConfig, from, to]
  );
  const themeConfig = useMemo(
    () => getMapThemeConfig(normalizedConfig.theme),
    [normalizedConfig.theme]
  );
  const prefersReducedMotion = useMemo(
    () => typeof window !== "undefined" && Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches),
    []
  );

  const cameraState = useMemo(
    () => computeCameraState({
      waypoints: normalizedConfig.waypoints,
      progress,
      activeStepIndex,
      baseZoom: zoom,
      routeStyle: normalizedConfig.routeStyle,
      prefersReducedMotion,
    }),
    [normalizedConfig.waypoints, normalizedConfig.routeStyle, progress, activeStepIndex, zoom, prefersReducedMotion]
  );

  const routeSplit = useMemo(
    () => splitMultiSegmentRoute(normalizedConfig.waypoints, normalizedConfig.routeStyle, progress),
    [normalizedConfig.waypoints, normalizedConfig.routeStyle, progress]
  );

  useEffect(() => {
    if (!mapRef.current) return;
    const tileLayer = new TileLayer({
      className: "ol-layer scrolly-map-tiles",
      preload: Infinity,
      source: new XYZ({ url: themeConfig.tileUrl }),
    });
    tileLayerRef.current = tileLayer;

    const extent = get("EPSG:3857").getExtent().slice();
    extent[0] += extent[0];
    extent[2] += extent[2];
    const initialCenter = normalizedConfig.waypoints[0]?.coordinate || from || [10.466, 61.115];

    const initialMap = new Map({
      target: mapRef.current,
      view: new View({ center: fromLonLat(initialCenter), zoom, extent }),
      layers: [tileLayer],
      controls: defaultControls({ zoom: false }),
      interactions: defaults({ mouseWheelZoom: false }),
    });
    setMap(initialMap);

    return () => {
      initialMap.setTarget(null);
      setMap(null);
    };
  }, []);

  useEffect(() => {
    if (tileLayerRef.current && themeConfig.tileUrl) {
      tileLayerRef.current.setSource(new XYZ({ url: themeConfig.tileUrl }));
    }
  }, [themeConfig.tileUrl]);

  useEffect(() => {
    const urls = [
      themeConfig.tileUrl,
      normalizedConfig.historicOverlay?.enabled ? normalizedConfig.historicOverlay?.url : null,
    ].filter(Boolean);

    prefetchRouteTiles({
      tileUrls: urls,
      waypoints: normalizedConfig.waypoints,
      baseZoom: zoom,
      routeStyle: normalizedConfig.routeStyle,
    });
  }, [
    themeConfig.tileUrl,
    normalizedConfig.historicOverlay?.enabled,
    normalizedConfig.historicOverlay?.url,
    normalizedConfig.waypoints,
    zoom,
    normalizedConfig.routeStyle,
  ]);

  useMapCameraAnimation({ map, edit, cameraState, routeSplit, prefersReducedMotion });

  const historicOpacity = useMemo(
    () => computeHistoricLayerOpacity({ waypoints: normalizedConfig.waypoints, activeStepIndex, progress }),
    [normalizedConfig.waypoints, activeStepIndex, progress]
  );

  useHistoricMapLayer({ map, historicOverlay: normalizedConfig.historicOverlay, opacity: historicOpacity });

  useFlightArc({
    map,
    counter: progress,
    waypoints: normalizedConfig.waypoints,
    from: normalizedConfig.from,
    to: normalizedConfig.to,
    routeStyle: normalizedConfig.routeStyle,
    theme: normalizedConfig.theme,
    edit,
  });

  useEditLayer({
    map,
    edit,
    waypoints: normalizedConfig.waypoints,
    from: normalizedConfig.from,
    to: normalizedConfig.to,
    onWaypointChange,
    onAdd,
    onChange,
  });

  return (
    <div style={{ position: "relative", height: "100%", width: "100%", overflow: "hidden" }} className={`map-wrapper ${className}`}>
      <style>{`.scrolly-map-tiles { filter: ${themeConfig.cssFilter || "none"}; } .scrolly-historic-overlay-layer { filter: ${normalizedConfig.historicOverlay?.filter || "none"}; } .ol-zoom { display: none !important; }`}</style>
      <div ref={mapRef} style={{ height: "100%", width: "100%" }} className="map-container" />
      {normalizedConfig.vignette && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background: "radial-gradient(circle at center, transparent 40%, rgba(0,0,0,0.4) 80%, rgba(0,0,0,0.7) 100%)",
            zIndex: 3,
          }}
        />
      )}
      {normalizedConfig.compassRose && <CompassRose />}
      <LivingPinsOverlay
        map={map}
        waypoints={normalizedConfig.waypoints}
        activeWaypointIndex={cameraState.activeWaypointIndex}
        prefersReducedMotion={prefersReducedMotion}
        onSelectWaypoint={onSelectWaypoint}
      />
    </div>
  );
};
